import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import { ChangePasswordModal, Enable2FAModal, LoginHistoryModal } from '../components/SecurityModals';
import './Settings.css';
import { useApp, useT, DEVISES, LANGUES } from '../context/AppContext';

const Settings = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const { devise, setDevise, langue, setLangue, avatar, updateAvatar } = useApp();
  const tr = useT();

  const [modals, setModals] = useState({ changePassword: false, enable2FA: false, loginHistory: false });
  const [notifications, setNotifications] = useState({
    email: true, push: false, sms: false,
    newFeatures: true, documentUpdates: true, tips: false, promotions: false,
  });
  const [formData, setFormData] = useState({
    name: user?.name || '', email: user?.email || '', phone: '', company: '', address: '',
  });
  const [accentColor, setAccentColor] = useState('purple');

  /* ── Paramètres documents + numérotation auto ── */
  const [docSettings, setDocSettings] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('doc_settings') || '{}');
      return {
        defaultFormat:  s.defaultFormat  || 'pdf',
        autoSave:       s.autoSave       !== undefined ? s.autoSave : true,
        invoicePrefix:  s.invoicePrefix  || 'INV',
        invoiceNextNum: s.invoiceNextNum || 1,
        devisPrefix:    s.devisPrefix    || 'DEV',
        devisNextNum:   s.devisNextNum   || 1,
      };
    } catch {
      return { defaultFormat: 'pdf', autoSave: true, invoicePrefix: 'INV', invoiceNextNum: 1, devisPrefix: 'DEV', devisNextNum: 1 };
    }
  });

  /* ── Suppression compte ── */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [docToDelete, setDocToDelete] = useState(null);
  const [notification, setNotification] = useState(null);

  const showNotif = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const loadDocuments = async () => {
    setDocumentsLoading(true);
    try { const res = await axios.get('/pdf'); setDocuments(res.data.pdfs || []); }
    catch { showNotif(tr('erreur_chargement'), 'error'); }
    finally { setDocumentsLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'documents') loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openModal  = name => setModals(p => ({ ...p, [name]: true }));
  const closeModal = name => setModals(p => ({ ...p, [name]: false }));
  const handleChange      = e => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleNotifChange = key => setNotifications(p => ({ ...p, [key]: !p[key] }));

  const handleAvatarChange = e => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => updateAvatar(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSaveDocSettings = () => {
    localStorage.setItem('doc_settings', JSON.stringify(docSettings));
    showNotif(tr('parametres_sauvegardes'));
  };

  const handleDeleteDocument = async id => {
    try { await axios.delete(`/pdf/${id}`); showNotif(tr('client_supprime')); loadDocuments(); }
    catch { showNotif(tr('erreur_suppression'), 'error'); }
    finally { setDocToDelete(null); }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'SUPPRIMER') { setDeleteError('Veuillez taper exactement "SUPPRIMER" pour confirmer.'); return; }
    setDeleteLoading(true); setDeleteError('');
    try { await axios.delete('/user/me'); logout(); navigate('/'); }
    catch (err) { setDeleteError(err.response?.data?.message || 'Erreur lors de la suppression.'); setDeleteLoading(false); }
  };

  const buildNum = (prefix, num) => `${prefix}-${new Date().getFullYear()}-${String(num).padStart(3, '0')}`;

  const tabs = [
    { id: 'profile',       icon: 'fa-user',      label: 'Profil' },
    { id: 'security',      icon: 'fa-shield-alt', label: 'Sécurité' },
    { id: 'notifications', icon: 'fa-bell',        label: 'Notifications' },
    { id: 'appearance',    icon: 'fa-palette',     label: 'Apparence' },
    { id: 'localisation',  icon: 'fa-globe',       label: 'Localisation' },
    { id: 'documents',     icon: 'fa-file-alt',    label: 'Documents' },
  ];

  return (
    <div className="page-with-sidebar">
      <Sidebar />

      {notification && (
        <div className={`settings-toast settings-toast-${notification.type}`}>
          <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
          {notification.message}
        </div>
      )}

      <main className="settings-main">
        <div className="settings-header">
          <div><h1><i className="fas fa-cog"></i> Paramètres</h1><p>Gérez vos préférences et votre compte</p></div>
        </div>

        <div className="settings-container">
          <div className="settings-sidebar">
            <nav className="settings-nav">
              {tabs.map(tab => (
                <button key={tab.id} className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                  <i className={`fas ${tab.icon}`}></i><span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="settings-content">

            {/* ══ PROFIL ══ */}
            {activeTab === 'profile' && (
              <div className="settings-section">
                <div className="section-header-settings">
                  <h2>Informations du Profil</h2><p>Mettez à jour vos informations personnelles</p>
                </div>
                <div className="profile-avatar-section">
                  <div className="avatar-large">
                    {avatar ? <img src={avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="avatar-actions">
                    <label className="btn-change-avatar">
                      <i className="fas fa-camera"></i> Changer la photo
                      <input type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />
                    </label>
                    {avatar && <button className="btn-remove-avatar" onClick={() => updateAvatar(null)}><i className="fas fa-trash"></i> Supprimer</button>}
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group-settings"><label>Nom complet</label><input className="input-settings" name="name" value={formData.name} onChange={handleChange} placeholder="Votre nom" /></div>
                  <div className="form-group-settings"><label>Email</label><input className="input-settings" name="email" type="email" value={formData.email} onChange={handleChange} /></div>
                  <div className="form-group-settings"><label>Téléphone</label><input className="input-settings" name="phone" value={formData.phone} onChange={handleChange} placeholder="+33 6 00 00 00 00" /></div>
                  <div className="form-group-settings"><label>Entreprise</label><input className="input-settings" name="company" value={formData.company} onChange={handleChange} /></div>
                  <div className="form-group-settings full-width"><label>Adresse</label><input className="input-settings" name="address" value={formData.address} onChange={handleChange} /></div>
                </div>
                <div className="settings-actions">
                  <button className="btn-save" onClick={() => showNotif(tr('parametres_sauvegardes'))}><i className="fas fa-save"></i> Sauvegarder</button>
                </div>
              </div>
            )}

            {/* ══ SÉCURITÉ ══ */}
            {activeTab === 'security' && (
              <div className="settings-section">
                <div className="section-header-settings"><h2>Confidentialité & Sécurité</h2><p>Gérez la sécurité de votre compte</p></div>
                <div className="security-card">
                  {[
                    { icon: 'fa-key',        title: 'Mot de passe',            desc: 'Modifiez votre mot de passe',               modal: 'changePassword', btnIcon: 'fa-edit',       btnLabel: 'Modifier' },
                    { icon: 'fa-mobile-alt', title: 'Authentification 2FA',    desc: 'Ajoutez une couche de sécurité',            modal: 'enable2FA',      btnIcon: 'fa-shield-alt', btnLabel: 'Activer' },
                    { icon: 'fa-history',    title: 'Historique de connexion', desc: 'Consultez vos dernières connexions',        modal: 'loginHistory',   btnIcon: 'fa-eye',        btnLabel: 'Voir' },
                  ].map(item => (
                    <div key={item.modal} className="security-item">
                      <div className="security-icon"><i className={`fas ${item.icon}`}></i></div>
                      <div className="security-info"><h3>{item.title}</h3><p>{item.desc}</p></div>
                      <button className="btn-security" onClick={() => openModal(item.modal)}><i className={`fas ${item.btnIcon}`}></i> {item.btnLabel}</button>
                    </div>
                  ))}
                  <div className="security-item security-item-danger">
                    <div className="security-icon danger"><i className="fas fa-trash-alt"></i></div>
                    <div className="security-info"><h3>Supprimer le compte</h3><p>Suppression définitive et irréversible</p></div>
                    <button className="btn-security danger" onClick={() => { setShowDeleteModal(true); setDeleteConfirm(''); setDeleteError(''); }}>
                      <i className="fas fa-trash-alt"></i> Supprimer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ══ NOTIFICATIONS ══ */}
            {activeTab === 'notifications' && (
              <div className="settings-section">
                <div className="section-header-settings"><h2>Notifications</h2><p>Choisissez comment être notifié</p></div>
                <div className="notifications-card">
                  {[
                    { key: 'email', icon: 'fa-envelope', title: 'Email',              desc: 'Notifications par email' },
                    { key: 'push',  icon: 'fa-bell',     title: 'Notifications push', desc: 'Notifications navigateur' },
                    { key: 'sms',   icon: 'fa-sms',      title: 'SMS',                desc: 'Alertes par SMS' },
                  ].map(n => (
                    <div key={n.key} className="notification-item">
                      <div className="security-icon" style={{ width: 44, height: 44, fontSize: '1.1rem' }}><i className={`fas ${n.icon}`}></i></div>
                      <div className="notification-info"><h3>{n.title}</h3><p>{n.desc}</p></div>
                      <label className="toggle-switch">
                        <input type="checkbox" checked={notifications[n.key]} onChange={() => handleNotifChange(n.key)} />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
                <div className="notification-types">
                  <h3>Types de notifications</h3>
                  {[
                    { key: 'newFeatures',     label: 'Nouvelles fonctionnalités' },
                    { key: 'documentUpdates', label: 'Mises à jour des documents' },
                    { key: 'tips',            label: 'Conseils et astuces' },
                    { key: 'promotions',      label: 'Promotions et offres' },
                  ].map(n => (
                    <div key={n.key} className="notification-type-item">
                      <label className="checkbox-label"><input type="checkbox" checked={notifications[n.key]} onChange={() => handleNotifChange(n.key)} />{n.label}</label>
                    </div>
                  ))}
                </div>
                <div className="settings-actions">
                  <button className="btn-save" onClick={() => showNotif(tr('parametres_sauvegardes'))}><i className="fas fa-save"></i> Sauvegarder</button>
                </div>
              </div>
            )}

            {/* ══ APPARENCE ══ */}
            {activeTab === 'appearance' && (
              <div className="settings-section">
                <div className="section-header-settings"><h2>Apparence</h2><p>Personnalisez l'interface</p></div>
                <div className="appearance-card">
                  <h3>Thème</h3>
                  <div className="theme-options">
                    <div className={`theme-card ${!isDarkMode ? 'active' : ''}`} onClick={toggleTheme}>
                      <div className="theme-preview light"><div className="theme-preview-bar"></div><div className="theme-preview-content"><div className="theme-preview-box"></div><div className="theme-preview-box"></div></div></div>
                      <div className="theme-info"><i className="fas fa-sun"></i><span>Mode Clair</span></div>
                    </div>
                    <div className={`theme-card ${isDarkMode ? 'active' : ''}`} onClick={toggleTheme}>
                      <div className="theme-preview dark"><div className="theme-preview-bar"></div><div className="theme-preview-content"><div className="theme-preview-box"></div><div className="theme-preview-box"></div></div></div>
                      <div className="theme-info"><i className="fas fa-moon"></i><span>Mode Sombre</span></div>
                    </div>
                  </div>
                </div>
                <div className="appearance-card">
                  <h3>Couleur d'accentuation</h3>
                  <div className="accent-colors">
                    {['purple', 'blue', 'green', 'red', 'orange'].map(color => (
                      <button key={color} className={`accent-color ${color} ${accentColor === color ? 'active' : ''}`}
                        onClick={() => { setAccentColor(color); document.documentElement.setAttribute('data-accent', color); }}>
                        {accentColor === color && <i className="fas fa-check"></i>}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="settings-actions">
                  <button className="btn-save" onClick={() => showNotif(tr('parametres_sauvegardes'))}><i className="fas fa-save"></i> Appliquer</button>
                </div>
              </div>
            )}

            {/* ══ LOCALISATION ══ */}
            {activeTab === 'localisation' && (
              <div className="settings-section">
                <div className="section-header-settings"><h2><i className="fas fa-globe"></i> {tr('langue')}</h2><p>{tr('choisir_langue')}</p></div>
                <div className="lang-grid">
                  {LANGUES.map(l => (
                    <button key={l.code} className={`lang-btn ${langue === l.code ? 'lang-btn-active' : ''}`} onClick={() => setLangue(l.code)}>
                      <span className="lang-flag">{l.flag}</span><span className="lang-label">{l.label}</span>
                      {langue === l.code && <i className="fas fa-check lang-check"></i>}
                    </button>
                  ))}
                </div>
                <div className="section-header-settings" style={{ marginTop: '2.5rem' }}>
                  <h2><i className="fas fa-coins"></i> {tr('devise_defaut')}</h2><p>{tr('choisir_devise')}</p>
                </div>
                <div className="devise-grid">
                  {DEVISES.map(d => (
                    <button key={d.code} className={`devise-btn ${devise === d.code ? 'devise-btn-active' : ''}`} onClick={() => setDevise(d.code)}>
                      <span className="devise-symbol">{d.symbol}</span><span className="devise-code">{d.code}</span><span className="devise-label">{d.label}</span>
                      {devise === d.code && <i className="fas fa-check devise-check"></i>}
                    </button>
                  ))}
                </div>
                <div className="settings-actions">
                  <button className="btn-save" onClick={() => showNotif(tr('parametres_sauvegardes'))}><i className="fas fa-save"></i> {tr('sauvegarder')}</button>
                </div>
              </div>
            )}

            {/* ══ DOCUMENTS ══ */}
            {activeTab === 'documents' && (
              <div className="settings-section">
                <div className="section-header-settings"><h2>Gestion des Documents</h2><p>Configurez vos préférences et consultez vos documents</p></div>

                {/* Format + Sauvegarde auto */}
                <div className="documents-card">
                  <div className="document-setting-item">
                    <div className="document-setting-info"><h3>Format par défaut</h3><p>Format d'export par défaut</p></div>
                    <select className="select-settings" style={{ maxWidth: '140px', flexShrink: 0 }} value={docSettings.defaultFormat}
                      onChange={e => setDocSettings(p => ({ ...p, defaultFormat: e.target.value }))}>
                      <option value="pdf">PDF</option><option value="docx">Word (.docx)</option>
                    </select>
                  </div>
                  <div className="document-setting-item">
                    <div className="document-setting-info"><h3>Sauvegarde automatique</h3><p>Sauvegardez automatiquement après génération</p></div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={docSettings.autoSave} onChange={() => setDocSettings(p => ({ ...p, autoSave: !p.autoSave }))} />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                {/* Numérotation automatique */}
                <div style={{ margin: '2rem 0' }}>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.4rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <i className="fas fa-hashtag" style={{ color: 'var(--accent-start)' }}></i> Numérotation automatique
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.87rem', marginBottom: '1.25rem' }}>
                    Le numéro s'incrémente automatiquement à chaque PDF généré.
                  </p>
                  <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2,1fr)', gap: '1.1rem', marginBottom: '1rem' }}>
                    <div className="form-group-settings">
                      <label>Préfixe facture</label>
                      <input className="input-settings" value={docSettings.invoicePrefix} maxLength={6}
                        onChange={e => setDocSettings(p => ({ ...p, invoicePrefix: e.target.value.toUpperCase() }))} placeholder="INV" />
                    </div>
                    <div className="form-group-settings">
                      <label>Prochain numéro facture</label>
                      <input type="number" className="input-settings" min={1} value={docSettings.invoiceNextNum}
                        onChange={e => setDocSettings(p => ({ ...p, invoiceNextNum: parseInt(e.target.value) || 1 }))} />
                    </div>
                    <div className="form-group-settings">
                      <label>Préfixe devis</label>
                      <input className="input-settings" value={docSettings.devisPrefix} maxLength={6}
                        onChange={e => setDocSettings(p => ({ ...p, devisPrefix: e.target.value.toUpperCase() }))} placeholder="DEV" />
                    </div>
                    <div className="form-group-settings">
                      <label>Prochain numéro devis</label>
                      <input type="number" className="input-settings" min={1} value={docSettings.devisNextNum}
                        onChange={e => setDocSettings(p => ({ ...p, devisNextNum: parseInt(e.target.value) || 1 }))} />
                    </div>
                  </div>

                  {/* Aperçu */}
                  <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                    {[
                      { label: 'Prochaine facture', prefix: docSettings.invoicePrefix || 'INV', num: docSettings.invoiceNextNum || 1 },
                      { label: 'Prochain devis',    prefix: docSettings.devisPrefix   || 'DEV', num: docSettings.devisNextNum   || 1 },
                    ].map(ex => (
                      <div key={ex.label} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 1rem', background: 'var(--bg-subtle)', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--border)', fontSize: '0.85rem' }}>
                        <i className="fas fa-tag" style={{ color: 'var(--accent-start)', fontSize: '0.72rem' }}></i>
                        <span style={{ color: 'var(--text-secondary)' }}>{ex.label} :</span>
                        <strong style={{ color: 'var(--accent-start)' }}>{buildNum(ex.prefix, ex.num)}</strong>
                      </div>
                    ))}
                  </div>

                  <div className="settings-actions">
                    <button className="btn-save" onClick={handleSaveDocSettings}><i className="fas fa-save"></i> Sauvegarder les paramètres</button>
                  </div>
                </div>

                {/* Liste documents */}
                <div className="documents-list-section">
                  <h3>Mes Documents <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 400 }}>({documents.length})</span></h3>
                  {documentsLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.5rem' }}></i> Chargement...
                    </div>
                  ) : documents.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                      <i className="fas fa-folder-open" style={{ fontSize: '2.5rem', opacity: 0.3, display: 'block', marginBottom: '1rem' }}></i>
                      <p>Aucun document sauvegardé.</p>
                    </div>
                  ) : (
                    <div className="documents-list">
                      {documents.map(doc => (
                        <div key={doc.id} className="document-item">
                          <div className="document-icon">
                            <i className={`fas ${doc.type === 'invoice' ? 'fa-file-invoice' : doc.type === 'quote' ? 'fa-file-contract' : doc.type === 'cv' ? 'fa-id-card' : 'fa-file-alt'}`}></i>
                          </div>
                          <div className="document-details">
                            <h4>{doc.fileName}</h4>
                            <div className="document-meta">
                              <span className="doc-type">{doc.type === 'invoice' ? 'Facture' : doc.type === 'quote' ? 'Devis' : doc.type === 'cv' ? 'CV' : doc.type}</span>
                              <span>{new Date(doc.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                            </div>
                          </div>
                          <div className="document-actions">
                            <button className="btn-doc-action delete" onClick={() => setDocToDelete(doc)} title="Supprimer"><i className="fas fa-trash-alt"></i></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Popup suppression document */}
        {docToDelete && (
          <div className="modal-overlay" onClick={() => setDocToDelete(null)}>
            <div className="modal-content-security" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
              <div className="modal-header-security danger">
                <h2><i className="fas fa-trash-alt"></i> Supprimer le document</h2>
                <button className="modal-close" onClick={() => setDocToDelete(null)}><i className="fas fa-times"></i></button>
              </div>
              <div className="delete-warning" style={{ padding: '1.5rem 2rem' }}>
                <div className="warning-icon"><i className="fas fa-exclamation-triangle"></i></div>
                <p style={{ textAlign: 'center', marginTop: '0.5rem' }}>Voulez-vous vraiment supprimer <strong>« {docToDelete.fileName} »</strong> ?</p>
                <p style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>Cette action est irréversible.</p>
              </div>
              <div className="modal-actions-security">
                <button className="btn-modal-cancel" onClick={() => setDocToDelete(null)}><i className="fas fa-arrow-left"></i> Annuler</button>
                <button className="btn-modal-confirm danger" onClick={() => handleDeleteDocument(docToDelete.id)}><i className="fas fa-trash-alt"></i> Supprimer définitivement</button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modale suppression compte */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content-security" onClick={e => e.stopPropagation()}>
            <div className="modal-header-security danger">
              <h2><i className="fas fa-exclamation-triangle"></i> Supprimer le compte</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="delete-warning">
              <div className="warning-icon"><i className="fas fa-exclamation-triangle"></i></div>
              <h3>Cette action est irréversible !</h3>
              <ul className="warning-list">
                <li>Tous vos documents seront supprimés définitivement</li>
                <li>Votre historique sera effacé</li>
                <li>Vous ne pourrez pas récupérer votre compte</li>
              </ul>
              <div className="form-group-settings" style={{ marginTop: '1.5rem' }}>
                <label>Tapez <strong>SUPPRIMER</strong> pour confirmer</label>
                <input className="input-settings" type="text" value={deleteConfirm} onChange={e => { setDeleteConfirm(e.target.value); setDeleteError(''); }} placeholder="SUPPRIMER" />
                {deleteError && <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.5rem' }}><i className="fas fa-exclamation-circle"></i> {deleteError}</p>}
              </div>
            </div>
            <div className="modal-actions-security">
              <button className="btn-modal-cancel" onClick={() => setShowDeleteModal(false)}>Annuler</button>
              <button className="btn-modal-confirm danger" onClick={handleDeleteAccount} disabled={deleteLoading || deleteConfirm !== 'SUPPRIMER'}>
                {deleteLoading ? <><i className="fas fa-spinner fa-spin"></i> Suppression...</> : <><i className="fas fa-trash-alt"></i> Supprimer définitivement</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal isOpen={modals.changePassword} onClose={() => closeModal('changePassword')} />
      <Enable2FAModal      isOpen={modals.enable2FA}      onClose={() => closeModal('enable2FA')} />
      <LoginHistoryModal   isOpen={modals.loginHistory}   onClose={() => closeModal('loginHistory')} />
    </div>
  );
};

export default Settings;